import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Paper,
    Button,
    CircularProgress,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Header } from '../Components/Shared/Header/Header';
import { Filters } from '../Components/Shared/Filters/Filters';
import { AvlTable } from '../Components/Shared/Table/Table';
import { HoverModal } from '../Components/BodyComponent/Alps/HoverModal';
import { AvlModal } from '../Components/Shared/AvlModal/AvlModal';
import { UrlButton, UrlActionType } from '../Components/Shared/UrlButton/UrlButton';
import { getAlpsFolders, downloadAlpsCsv, updateAlpsNote } from '../Services/Alps.services';
import { fieldNames } from '../Components/Shared/Filters/Filters';
import { format } from 'date-fns';
import { datetimeToFormattedString } from '../Utils/Utils';
import { NEW_TAB_DOMAIN } from '../settings';
import { AlpsNoteField } from '../Components/BodyComponent/Alps/AlpsNoteField';
import { dateFormat } from '../Constants/Date.constant';

const getNumericData = (value) => {
    return value !== undefined ? value : 'N/A';
};

const AlpsProjects = () => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [isHoverModalOpen, setIsHoverModalOpen] = useState(false);
    const [hoveredFolder, setHoveredFolder] = useState(null);
    const [isAvlModalOpen, setIsAvlModalOpen] = useState(false);
    const [note, setNote] = useState('');
    const [isNoteLoading, setIsNoteLoading] = useState(false);
    const [filters, setFilters] = useState({
        [fieldNames.text]: '',
        [fieldNames.startDate]: null,
        [fieldNames.endDate]: null,
    });

    const buildFilterParams = (includePagination = false) => {
        const params = {
            query: filters[fieldNames.text],
            startDate: filters[fieldNames.startDate] ? format(filters[fieldNames.startDate], dateFormat) : null,
            endDate: filters[fieldNames.endDate] ? format(filters[fieldNames.endDate], dateFormat) : null,
        };
        if (includePagination) {
            params.limit = rowsPerPage;
            params.offset = (page - 1) * rowsPerPage;
        }
        return params;
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await getAlpsFolders(buildFilterParams(true));
            setData(response);
        } catch (error) {
            console.error('Error fetching ALPS folders:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage, filters]);

    useEffect(() => {
        if (hoveredFolder) {
            setNote(hoveredFolder.alpsNote || '');
        }
    }, [hoveredFolder]);

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(1);
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const response = await downloadAlpsCsv(buildFilterParams(false));
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `alps-projects-${format(new Date(), dateFormat)}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading CSV:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleOpenAvlModal = (folder) => {
        setHoveredFolder(folder);
        setIsAvlModalOpen(true);
        setIsHoverModalOpen(false);
    };

    const handleSaveNote = async (folderId, newNote) => {
        setIsNoteLoading(true);
        try {
            await updateAlpsNote(folderId, newNote);
            fetchData();
        } catch (error) {
            console.error('Error saving note:', error);
        } finally {
            setIsNoteLoading(false);
        }
    };

    const handleCloseAvlModal = () => {
        setIsAvlModalOpen(false);
        setHoveredFolder(null);
    };

    const tableHeaders = [
        { id: 'createdAt', label: 'Created At', align: 'left' },
        { id: 'organisation', label: 'Organisation', align: 'left' },
        { id: 'userName', label: 'User', align: 'left' },
        { id: 'projectName', label: 'Project Name', align: 'left' },
        { id: 'matterNumber', label: 'Matter Number', align: 'left' },
        { id: 'folderId', label: 'Folder', align: 'center', width: '24px' },
        { id: 'numberOfTitles', label: 'Titles', align: 'center', width: '40px' },
        { id: 'numberOfPlans', label: 'Plans', align: 'center', width: '40px' },
        { id: 'numberOfScottishTitles', label: 'Scottish Titles', align: 'center', width: '40px' },
        { id: 'numberOfScottishPlans', label: 'Scottish Plans', align: 'center', width: '40px' },
        { id: 'numberOfOthers', label: 'Other Documents', align: 'center', width: '40px' },
        { id: 'numberOfFiledCopies', label: 'Filed Copies', align: 'center', width: '40px' },
        { id: 'numberOfLeases', label: 'Leases', align: 'center', width: '40px' },
        { id: 'alpsNote', label: 'ALPS Notes', align: 'left' },
    ];

    const tableRows = useMemo(() => {
        if (!data?.folders) return [];

        return data.folders.map((folder) => ({
            id: folder.folderId,
            cells: [
                { data: datetimeToFormattedString(folder.createdAt, 'None'), align: 'left' },
                { data: folder.organisation, align: 'left' },
                { data: folder.userName, align: 'left' },
                { data: folder.projectName, align: 'left' },
                { data: folder.matterNumber, align: 'left' },
                {
                    data: folder.folderId ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <UrlButton
                                buttonStyles={{
                                    link: { width: '32px', height: '32px' },
                                }}
                                description={`Copy to clipboard "${folder.folderId}"`}
                                action={UrlActionType.copyToClipboard}
                                url={folder.folderId}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2">N/A</Typography>
                    ),
                    align: 'center',
                },
                { data: getNumericData(folder.numberOfTitles), align: 'center', style: { width: '40px' } },
                { data: getNumericData(folder.numberOfPlans), align: 'center', style: { width: '40px' } },
                { data: getNumericData(folder.numberOfScottishTitles), align: 'center', style: { width: '40px' } },
                { data: getNumericData(folder.numberOfScottishPlans), align: 'center', style: { width: '40px' } },
                { data: getNumericData(folder.numberOfOthers), align: 'center', style: { width: '40px' } },
                { data: getNumericData(folder.numberOfFiledCopies), align: 'center', style: { width: '40px' } },
                { data: getNumericData(folder.numberOfLeases), align: 'center', style: { width: '40px' } },
                {
                    data: folder.alpsNote ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                            <Box
                                onMouseEnter={() => {
                                    setHoveredFolder(folder);
                                    setIsHoverModalOpen(true);
                                }}
                                onMouseLeave={() => setIsHoverModalOpen(false)}
                                onClick={() => handleOpenAvlModal(folder)}
                                sx={{ cursor: 'pointer', width: '100%' }}
                            >
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                    {folder.alpsNote.replace(/\n/g, ' ')}
                                </Typography>
                            </Box>
                            <Tooltip title="Edit ALPS Note">
                                <IconButton size="small" onClick={() => handleOpenAvlModal(folder)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Tooltip title="Add ALPS Note">
                            <IconButton size="small" onClick={() => handleOpenAvlModal(folder)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ),
                    align: 'left',
                },
            ],
        }));
    }, [data]);

    return (
        <Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
            <Card component={Paper}>
                <CardContent>
                    <div className="header">
                        <Header
                            title="ALPS Projects"
                            actionButtons={[
                                <Button
                                    key="download-csv-button"
                                    variant="contained"
                                    onClick={handleDownload}
                                    disabled={isDownloading || isLoading}
                                    startIcon={isDownloading ? <CircularProgress size={20} /> : null}
                                >
                                    {isDownloading ? 'Downloading...' : 'Download CSV'}
                                </Button>,
                            ]}
                            filters={<Filters isText isDateRange onFiltersChange={handleFilterChange} />}
                        />
                    </div>

                    {/* Table Section */}
                    <Box sx={{ width: '100%' }}>
                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <AvlTable
                                headers={tableHeaders}
                                rows={tableRows}
                                pageCount={data ? Math.ceil(data.count / rowsPerPage) : 0}
                                page={page}
                                onPageChange={handlePageChange}
                                onChangeRowsPerPage={handleRowsPerPageChange}
                                rowsPerPage={rowsPerPage}
                                rowsCount={data?.count || 0}
                                isSortEnabled={true}
                            />
                        )}
                    </Box>
                    <HoverModal open={isHoverModalOpen}>
                        {(isHoverModalOpen || hoveredFolder) && (
                            <AlpsNoteField
                                note={isHoverModalOpen ? note : hoveredFolder?.alpsNote || ''}
                                onChange={setNote}
                                loading={isNoteLoading}
                                disableActions={!isHoverModalOpen}
                            />
                        )}
                    </HoverModal>

                    <AvlModal
                        isOpen={isAvlModalOpen}
                        onClose={handleCloseAvlModal}
                        title={`${hoveredFolder?.projectName || ''}`}
                        actionButtons={[
                            <Button
                                key="cancel-btn"
                                onClick={handleCloseAvlModal}
                                disabled={isNoteLoading}
                                variant="outlined"
                            >
                                Cancel
                            </Button>,
                            <Button
                                key="save-btn"
                                onClick={() => handleSaveNote(hoveredFolder?.folderId, note)}
                                disabled={isNoteLoading}
                                variant="contained"
                                startIcon={isNoteLoading ? <CircularProgress size={20} /> : null}
                            >
                                {isNoteLoading ? 'Saving...' : 'Save'}
                            </Button>,
                        ]}
                    >
                        {isAvlModalOpen && (
                            <>
                                <AlpsNoteField
                                    note={note}
                                    onChange={setNote}
                                    loading={isNoteLoading}
                                    disableActions={false}
                                />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 2, display: 'block' }}
                                >
                                    New lines are supported. Use Enter to add line breaks.
                                </Typography>
                            </>
                        )}
                    </AvlModal>
                </CardContent>
            </Card>
        </Grid>
    );
};

export { AlpsProjects };
